import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useFinance } from "./FinanceContext";
import { rangeFor } from "./data/model";
import { MONTHS, CUR_MONTH_IDX } from "./data/seedData";
import type { LockInfo } from "./data/types";
import { StatusPill } from "./components/StatusChip";

const LEVELS: LockInfo["level"][] = ["Month", "Quarter", "Half-Year", "Year"];

export function PeriodControlPage() {
  const { locks, doFreeze, doUnlock, has, role, fmt, model } = useFinance();
  const canFreeze = has("freeze.month");
  const canUnlock = has("period.unlock");
  const [level, setLevel] = useState<LockInfo["level"]>("Month");
  const [anchor, setAnchor] = useState(CUR_MONTH_IDX);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Period Locking &amp; Freeze Control
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Lock financial periods to enforce immutability after approval. Month → Quarter → Half-Year →
        Year close. Reopen requires CEO/Director.
      </Typography>
      {!canFreeze ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your role ({role}) cannot freeze or reopen periods. Finance Head, Director and CEO can.
        </Alert>
      ) : null}

      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Freeze a Period
        </Typography>
        <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.5}>
          <TextField
            select
            size="small"
            label="Level"
            value={level}
            disabled={!canFreeze}
            onChange={(e) => setLevel(e.target.value as LockInfo["level"])}
          >
            {LEVELS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Anchor month"
            value={anchor}
            disabled={!canFreeze || level === "Year"}
            onChange={(e) => setAnchor(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <MenuItem key={m} value={i}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" disabled={!canFreeze} onClick={() => doFreeze(level, anchor)}>
            Freeze {level}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {level === "Year"
              ? "Locks the full FY"
              : "Locks: " +
                rangeFor(level, anchor)
                  .map((m) => MONTHS[m]?.split(" ")[0])
                  .join(", ")}
          </Typography>
        </Stack>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Locked By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MONTHS.map((m, i) => {
              const lock = locks[i];
              let b = 0;
              let a = 0;
              model.items.forEach((it) => {
                b += it.planned[i] ?? 0;
                a += it.actual[i] ?? 0;
              });
              return (
                <TableRow key={m}>
                  <TableCell sx={{ fontWeight: 600 }}>{m}</TableCell>
                  <TableCell>
                    <StatusPill status={lock ? `Locked · ${lock.level}` : "Editable"} />
                  </TableCell>
                  <TableCell>{lock ? lock.by : "—"}</TableCell>
                  <TableCell>{lock ? lock.date : "—"}</TableCell>
                  <TableCell align="right">{fmt(b)}</TableCell>
                  <TableCell align="right">{fmt(a)}</TableCell>
                  <TableCell>
                    {lock ? (
                      canUnlock ? (
                        <Button size="small" onClick={() => doUnlock(i)}>
                          Reopen
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          CEO/Director only
                        </Typography>
                      )
                    ) : canFreeze ? (
                      <Button size="small" onClick={() => doFreeze("Month", i)}>
                        Lock
                      </Button>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
